import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Slider } from "../ui/slider";
import {
  X,
  Trash2,
  Copy,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Bold,
  Plus,
  Minus,
} from "lucide-react";

const BlockEditor = ({ block, sectionId, onUpdate, onDelete, onDuplicate, onClose }) => {
  const renderEditor = () => {
    switch (block.type) {
      case "text":
        return <TextBlockEditor block={block} onUpdate={onUpdate} />;
      case "image":
        return <ImageBlockEditor block={block} onUpdate={onUpdate} />;
      case "button":
        return <ButtonBlockEditor block={block} onUpdate={onUpdate} />;
      case "divider":
        return <DividerBlockEditor block={block} onUpdate={onUpdate} />;
      case "spacer":
        return <SpacerBlockEditor block={block} onUpdate={onUpdate} />;
      case "social":
        return <SocialBlockEditor block={block} onUpdate={onUpdate} />;
      case "columns":
        return <ColumnsBlockEditor block={block} onUpdate={onUpdate} />;
      case "list":
        return <ListBlockEditor block={block} onUpdate={onUpdate} />;
      case "html":
        return <HtmlBlockEditor block={block} onUpdate={onUpdate} />;
      default:
        return <p className="text-muted-foreground">No editor available</p>;
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-3 border-b border-border/50 flex items-center justify-between">
        <h3 className="font-medium text-sm capitalize">{block.type} Block</h3>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onDuplicate}>
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive hover:text-destructive"
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Editor Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">{renderEditor()}</div>
    </div>
  );
};

// Text Block Editor
const TextBlockEditor = ({ block, onUpdate }) => (
  <div className="space-y-4">
    <div className="space-y-2">
      <Label className="text-xs">Content</Label>
      <Textarea
        value={block.content?.replace(/<[^>]*>/g, "") || ""}
        onChange={(e) => onUpdate({ content: e.target.value })}
        rows={4}
        className="text-sm"
        placeholder="Enter your text..."
      />
    </div>

    <div className="space-y-2">
      <Label className="text-xs">Font Size</Label>
      <Select
        value={block.fontSize || "16px"}
        onValueChange={(v) => onUpdate({ fontSize: v })}
      >
        <SelectTrigger className="h-8 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="12px">Small (12px)</SelectItem>
          <SelectItem value="14px">Normal (14px)</SelectItem>
          <SelectItem value="16px">Medium (16px)</SelectItem>
          <SelectItem value="18px">Large (18px)</SelectItem>
          <SelectItem value="24px">Heading (24px)</SelectItem>
          <SelectItem value="32px">Title (32px)</SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-2">
      <Label className="text-xs">Text Align</Label>
      <div className="flex gap-1">
        {["left", "center", "right"].map((align) => (
          <Button
            key={align}
            variant={block.textAlign === align ? "secondary" : "outline"}
            size="sm"
            className="flex-1"
            onClick={() => onUpdate({ textAlign: align })}
          >
            {align === "left" && <AlignLeft className="h-4 w-4" />}
            {align === "center" && <AlignCenter className="h-4 w-4" />}
            {align === "right" && <AlignRight className="h-4 w-4" />}
          </Button>
        ))}
      </div>
    </div>

    <div className="space-y-2">
      <Label className="text-xs">Font Weight</Label>
      <div className="flex gap-1">
        <Button
          variant={block.fontWeight === "normal" ? "secondary" : "outline"}
          size="sm"
          className="flex-1"
          onClick={() => onUpdate({ fontWeight: "normal" })}
        >
          Normal
        </Button>
        <Button
          variant={block.fontWeight === "bold" ? "secondary" : "outline"}
          size="sm"
          className="flex-1"
          onClick={() => onUpdate({ fontWeight: "bold" })}
        >
          <Bold className="h-4 w-4 mr-1" />
          Bold
        </Button>
      </div>
    </div>

    <div className="space-y-2">
      <Label className="text-xs">Text Color</Label>
      <div className="flex gap-2">
        <input
          type="color"
          value={block.textColor || "#1f2937"}
          onChange={(e) => onUpdate({ textColor: e.target.value })}
          className="w-8 h-8 rounded cursor-pointer"
        />
        <Input
          value={block.textColor || "#1f2937"}
          onChange={(e) => onUpdate({ textColor: e.target.value })}
          className="h-8 text-sm flex-1"
        />
      </div>
    </div>
  </div>
);

// Image Block Editor
const ImageBlockEditor = ({ block, onUpdate }) => (
  <div className="space-y-4">
    <div className="space-y-2">
      <Label className="text-xs">Image URL</Label>
      <Input
        value={block.src || ""}
        onChange={(e) => onUpdate({ src: e.target.value })}
        placeholder="https://example.com/image.jpg"
        className="h-8 text-sm"
      />
    </div>

    <div className="space-y-2">
      <Label className="text-xs">Alt Text</Label>
      <Input
        value={block.alt || ""}
        onChange={(e) => onUpdate({ alt: e.target.value })}
        placeholder="Image description"
        className="h-8 text-sm"
      />
    </div>

    <div className="space-y-2">
      <Label className="text-xs">Width</Label>
      <Select
        value={block.width || "100%"}
        onValueChange={(v) => onUpdate({ width: v })}
      >
        <SelectTrigger className="h-8 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="100%">Full Width</SelectItem>
          <SelectItem value="75%">75%</SelectItem>
          <SelectItem value="50%">50%</SelectItem>
          <SelectItem value="300px">300px</SelectItem>
          <SelectItem value="200px">200px</SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-2">
      <Label className="text-xs">Alignment</Label>
      <div className="flex gap-1">
        {["left", "center", "right"].map((align) => (
          <Button
            key={align}
            variant={block.alignment === align ? "secondary" : "outline"}
            size="sm"
            className="flex-1"
            onClick={() => onUpdate({ alignment: align })}
          >
            {align === "left" && <AlignLeft className="h-4 w-4" />}
            {align === "center" && <AlignCenter className="h-4 w-4" />}
            {align === "right" && <AlignRight className="h-4 w-4" />}
          </Button>
        ))}
      </div>
    </div>

    <div className="space-y-2">
      <Label className="text-xs">Border Radius</Label>
      <Select
        value={block.borderRadius || "0"}
        onValueChange={(v) => onUpdate({ borderRadius: v })}
      >
        <SelectTrigger className="h-8 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="0">None</SelectItem>
          <SelectItem value="4px">Small</SelectItem>
          <SelectItem value="8px">Medium</SelectItem>
          <SelectItem value="16px">Large</SelectItem>
          <SelectItem value="50%">Circle</SelectItem>
        </SelectContent>
      </Select>
    </div>
  </div>
);

// Button Block Editor
const ButtonBlockEditor = ({ block, onUpdate }) => (
  <div className="space-y-4">
    <div className="space-y-2">
      <Label className="text-xs">Button Text</Label>
      <Input
        value={block.text || ""}
        onChange={(e) => onUpdate({ text: e.target.value })}
        placeholder="Click Here"
        className="h-8 text-sm"
      />
    </div>

    <div className="space-y-2">
      <Label className="text-xs">Link URL</Label>
      <Input
        value={block.href || ""}
        onChange={(e) => onUpdate({ href: e.target.value })}
        placeholder="https://example.com"
        className="h-8 text-sm"
      />
    </div>

    <div className="space-y-2">
      <Label className="text-xs">Alignment</Label>
      <div className="flex gap-1">
        {["left", "center", "right"].map((align) => (
          <Button
            key={align}
            variant={block.alignment === align ? "secondary" : "outline"}
            size="sm"
            className="flex-1"
            onClick={() => onUpdate({ alignment: align })}
          >
            {align === "left" && <AlignLeft className="h-4 w-4" />}
            {align === "center" && <AlignCenter className="h-4 w-4" />}
            {align === "right" && <AlignRight className="h-4 w-4" />}
          </Button>
        ))}
      </div>
    </div>

    <div className="space-y-2">
      <Label className="text-xs">Background Color</Label>
      <div className="flex gap-2">
        <input
          type="color"
          value={block.backgroundColor || "#1CBCF8"}
          onChange={(e) => onUpdate({ backgroundColor: e.target.value })}
          className="w-8 h-8 rounded cursor-pointer"
        />
        <Input
          value={block.backgroundColor || "#1CBCF8"}
          onChange={(e) => onUpdate({ backgroundColor: e.target.value })}
          className="h-8 text-sm flex-1"
        />
      </div>
    </div>

    <div className="space-y-2">
      <Label className="text-xs">Text Color</Label>
      <div className="flex gap-2">
        <input
          type="color"
          value={block.textColor || "#ffffff"}
          onChange={(e) => onUpdate({ textColor: e.target.value })}
          className="w-8 h-8 rounded cursor-pointer"
        />
        <Input
          value={block.textColor || "#ffffff"}
          onChange={(e) => onUpdate({ textColor: e.target.value })}
          className="h-8 text-sm flex-1"
        />
      </div>
    </div>

    <div className="space-y-2">
      <Label className="text-xs">Border Radius</Label>
      <Select
        value={block.borderRadius || "6px"}
        onValueChange={(v) => onUpdate({ borderRadius: v })}
      >
        <SelectTrigger className="h-8 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="0">Square</SelectItem>
          <SelectItem value="4px">Slightly Rounded</SelectItem>
          <SelectItem value="6px">Rounded</SelectItem>
          <SelectItem value="20px">Pill</SelectItem>
        </SelectContent>
      </Select>
    </div>
  </div>
);

// Divider Block Editor
const DividerBlockEditor = ({ block, onUpdate }) => (
  <div className="space-y-4">
    <div className="space-y-2">
      <Label className="text-xs">Style</Label>
      <Select
        value={block.style || "solid"}
        onValueChange={(v) => onUpdate({ style: v })}
      >
        <SelectTrigger className="h-8 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="solid">Solid</SelectItem>
          <SelectItem value="dashed">Dashed</SelectItem>
          <SelectItem value="dotted">Dotted</SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-2">
      <Label className="text-xs">Thickness</Label>
      <Select
        value={block.thickness || "1px"}
        onValueChange={(v) => onUpdate({ thickness: v })}
      >
        <SelectTrigger className="h-8 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="1px">Thin (1px)</SelectItem>
          <SelectItem value="2px">Medium (2px)</SelectItem>
          <SelectItem value="3px">Thick (3px)</SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-2">
      <Label className="text-xs">Color</Label>
      <div className="flex gap-2">
        <input
          type="color"
          value={block.color || "#e5e7eb"}
          onChange={(e) => onUpdate({ color: e.target.value })}
          className="w-8 h-8 rounded cursor-pointer"
        />
        <Input
          value={block.color || "#e5e7eb"}
          onChange={(e) => onUpdate({ color: e.target.value })}
          className="h-8 text-sm flex-1"
        />
      </div>
    </div>
  </div>
);

// Spacer Block Editor
const SpacerBlockEditor = ({ block, onUpdate }) => (
  <div className="space-y-4">
    <div className="space-y-2">
      <Label className="text-xs">Height: {block.height || "20px"}</Label>
      <Slider
        value={[parseInt(block.height) || 20]}
        min={10}
        max={100}
        step={5}
        onValueChange={([v]) => onUpdate({ height: `${v}px` })}
      />
    </div>
  </div>
);

// Social Block Editor
const SocialBlockEditor = ({ block, onUpdate }) => {
  const socials = block.socials || [
    { type: "facebook", url: "" },
    { type: "twitter", url: "" },
    { type: "linkedin", url: "" },
  ];

  const updateSocial = (index, field, value) => {
    const newSocials = [...socials];
    newSocials[index] = { ...newSocials[index], [field]: value };
    onUpdate({ socials: newSocials });
  };

  const addSocial = () => {
    onUpdate({ socials: [...socials, { type: "website", url: "" }] });
  };

  const removeSocial = (index) => {
    onUpdate({ socials: socials.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-xs">Social Links</Label>
        {socials.map((social, index) => (
          <div key={index} className="flex gap-2 items-center">
            <Select
              value={social.type}
              onValueChange={(v) => updateSocial(index, "type", v)}
            >
              <SelectTrigger className="h-8 text-sm w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="facebook">Facebook</SelectItem>
                <SelectItem value="twitter">Twitter</SelectItem>
                <SelectItem value="linkedin">LinkedIn</SelectItem>
                <SelectItem value="instagram">Instagram</SelectItem>
                <SelectItem value="youtube">YouTube</SelectItem>
                <SelectItem value="website">Website</SelectItem>
              </SelectContent>
            </Select>
            <Input
              value={social.url}
              onChange={(e) => updateSocial(index, "url", e.target.value)}
              placeholder="URL"
              className="h-8 text-sm flex-1"
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => removeSocial(index)}
            >
              <Minus className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={addSocial} className="w-full">
          <Plus className="h-4 w-4 mr-1" />
          Add Social
        </Button>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Icon Style</Label>
        <Select
          value={block.iconStyle || "circle"}
          onValueChange={(v) => onUpdate({ iconStyle: v })}
        >
          <SelectTrigger className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="circle">Circle</SelectItem>
            <SelectItem value="square">Square</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Icon Color</Label>
        <div className="flex gap-2">
          <input
            type="color"
            value={block.iconBackground || "#1CBCF8"}
            onChange={(e) => onUpdate({ iconBackground: e.target.value })}
            className="w-8 h-8 rounded cursor-pointer"
          />
          <Input
            value={block.iconBackground || "#1CBCF8"}
            onChange={(e) => onUpdate({ iconBackground: e.target.value })}
            className="h-8 text-sm flex-1"
          />
        </div>
      </div>
    </div>
  );
};

// Columns Block Editor
const ColumnsBlockEditor = ({ block, onUpdate }) => (
  <div className="space-y-4">
    <div className="space-y-2">
      <Label className="text-xs">Number of Columns</Label>
      <Select
        value={String(block.columns || 2)}
        onValueChange={(v) => onUpdate({ columns: parseInt(v) })}
      >
        <SelectTrigger className="h-8 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="2">2 Columns</SelectItem>
          <SelectItem value="3">3 Columns</SelectItem>
          <SelectItem value="4">4 Columns</SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-2">
      <Label className="text-xs">Gap</Label>
      <Select
        value={block.gap || "20px"}
        onValueChange={(v) => onUpdate({ gap: v })}
      >
        <SelectTrigger className="h-8 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="10px">Small</SelectItem>
          <SelectItem value="20px">Medium</SelectItem>
          <SelectItem value="30px">Large</SelectItem>
        </SelectContent>
      </Select>
    </div>
  </div>
);

// List Block Editor
const ListBlockEditor = ({ block, onUpdate }) => {
  const items = block.items || ["Item 1", "Item 2", "Item 3"];

  const updateItem = (index, value) => {
    const newItems = [...items];
    newItems[index] = value;
    onUpdate({ items: newItems });
  };

  const addItem = () => {
    onUpdate({ items: [...items, "New item"] });
  };

  const removeItem = (index) => {
    onUpdate({ items: items.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-xs">List Type</Label>
        <Select
          value={block.listType || "bullet"}
          onValueChange={(v) => onUpdate({ listType: v })}
        >
          <SelectTrigger className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="bullet">Bullet Points</SelectItem>
            <SelectItem value="numbered">Numbered</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Items</Label>
        {items.map((item, index) => (
          <div key={index} className="flex gap-2">
            <Input
              value={item}
              onChange={(e) => updateItem(index, e.target.value)}
              className="h-8 text-sm flex-1"
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => removeItem(index)}
            >
              <Minus className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={addItem} className="w-full">
          <Plus className="h-4 w-4 mr-1" />
          Add Item
        </Button>
      </div>
    </div>
  );
};

// HTML Block Editor
const HtmlBlockEditor = ({ block, onUpdate }) => (
  <div className="space-y-4">
    <div className="space-y-2">
      <Label className="text-xs">Custom HTML</Label>
      <Textarea
        value={block.html || ""}
        onChange={(e) => onUpdate({ html: e.target.value })}
        rows={8}
        className="text-sm font-mono"
        placeholder="<div>Your HTML here...</div>"
      />
    </div>
  </div>
);

export default BlockEditor;
